import {
  format,
  differenceInYears,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  nextMonday,
  nextTuesday,
  nextWednesday,
  nextThursday,
  nextFriday,
  nextSaturday,
  nextSunday,
  startOfDay,
  parseISO,
  isBefore
} from 'date-fns';
import * as Sentry from '@sentry/browser';
import { TCalendarView, TConsultationStatus } from 'utils/types';
import {
  GENDER_OPTIONS,
  LIFESTYLE_ADVICE,
  REFERRAL_TIMEFRAME,
  REFERRAL_TYPE
} from 'utils/constants';
import {
  ICreateSchedule,
  IDoctorDateSchedule,
  IFileCheckResponse,
  IMedicationProps,
  IPatientBackground,
  IPatientDetails,
  IPatientHistory,
  ISelectOptions
} from 'utils/models';
import PhotoPlaceholderDoctor from 'assets/images/profile-hazel.png';
import PhotoPlaceholderPatient from 'assets/images/profile-hazel.png';
import { isPlainObject } from 'is-plain-object';
import { CONF_API_URL, CONF_ENVIRONMENT } from './config';

// Sentry logging helpers for Zoom video call issues
type SentryLogLevel = 'error' | 'warning' | 'info';

interface ZoomSentryContext {
  component: string;
  action: string;
  consultationId?: string;
  referenceId?: string | number;
  userId?: string | number;
  userName?: string;
  [key: string]: any;
}

/**
 * Logs zoom-related events to Sentry with standardized context
 * @param message - The message to log
 * @param level - The severity level (error, warning, info)
 * @param context - Additional context data
 */
export const logZoomEventToSentry = (
  message: string,
  level: SentryLogLevel = 'info',
  context: ZoomSentryContext
) => {
  const { component, action, ...extra } = context;

  Sentry.captureMessage(message, {
    level,
    tags: {
      project: 'hazel-doctor',
      component,
      action,
      category: 'zoom-video-call'
    },
    extra
  });
};

/**
 * Logs zoom-related errors to Sentry with standardized context
 * @param error - The error object
 * @param context - Additional context data
 */
export const logZoomErrorToSentry = (error: Error | unknown, context: ZoomSentryContext) => {
  const { component, action, ...extra } = context;

  Sentry.captureException(error, {
    tags: {
      project: 'hazel-doctor',
      component,
      action,
      category: 'zoom-video-call'
    },
    extra
  });
};

/**
 * Adds a breadcrumb for zoom navigation/flow tracking
 * @param message - Breadcrumb message
 * @param data - Additional data
 */
export const addZoomBreadcrumb = (message: string, data?: Record<string, any>) => {
  if (CONF_ENVIRONMENT === 'production') {
    Sentry.addBreadcrumb({
      category: 'zoom-video-call',
      message,
      level: 'info',
      data
    });
  }
};

export const isMobile = (() => {
  if (typeof navigator === 'undefined' || typeof navigator.userAgent !== 'string') {
    return false;
  }
  return /Mobile/.test(navigator.userAgent);
})();

export const ensureMediaPermissionsWithDetails = async (
  name: string,
  consultationId: string | undefined,
  referenceId: string | undefined
) => {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const shouldAskForMediaPermissions = devices.every(
      (device) => !(device.deviceId && device.label)
    );

    if (shouldAskForMediaPermissions) {
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: true
        });

        // Stop all tracks
        mediaStream.getTracks().forEach((track) => track.stop());
        return undefined;
      } catch (error) {
        // Detailed error logging for permission denial
        const errorName = error instanceof Error ? error.name : 'Unknown';
        const errorMessage = error instanceof Error ? error.message : String(error);

        logZoomEventToSentry(
          `Doctor ${name} denied camera and audio permission for consultation ID: ${consultationId} with reference ID: ${referenceId}`,
          'warning',
          {
            component: 'ensureMediaPermissionsWithDetails',
            action: 'mediaPermissionDenied',
            consultationId,
            referenceId,
            userName: name,
            errorType: errorName,
            errorMessage,
            browser: navigator.userAgent,
            platform: navigator.platform
          }
        );

        return error;
      }
    }

    return undefined;
  } catch (error) {
    // Log enumeration errors
    logZoomEventToSentry(`Failed to enumerate media devices for doctor ${name}`, 'error', {
      component: 'ensureMediaPermissionsWithDetails',
      action: 'enumerateDevicesFailed',
      consultationId,
      referenceId,
      userName: name,
      error: error instanceof Error ? error.message : String(error),
      browser: navigator.userAgent
    });

    return error;
  }
};

export const ensureMediaPermissions = async () => {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const shouldAskForMediaPermissions = devices.every(
      (device) => !(device.deviceId && device.label)
    );

    if (shouldAskForMediaPermissions) {
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: true
        });

        // Stop all tracks
        mediaStream.getTracks().forEach((track) => track.stop());
        return undefined;
      } catch (error) {
        // Detailed error logging for permission denial
        const errorName = error instanceof Error ? error.name : 'Unknown';
        const errorMessage = error instanceof Error ? error.message : String(error);

        logZoomEventToSentry('Doctor denied camera and audio permission', 'warning', {
          component: 'ensureMediaPermissions',
          action: 'mediaPermissionDenied',
          errorType: errorName,
          errorMessage,
          browser: navigator.userAgent,
          platform: navigator.platform,
          deviceCount: devices.length
        });

        return error;
      }
    }

    return undefined;
  } catch (error) {
    // Log enumeration errors
    logZoomEventToSentry('Failed to enumerate media devices', 'error', {
      component: 'ensureMediaPermissions',
      action: 'enumerateDevicesFailed',
      error: error instanceof Error ? error.message : String(error),
      browser: navigator.userAgent
    });

    return error;
  }
};

// Recursively removes any object keys with a value of undefined
export function removeUndefineds<T>(obj: T): T {
  if (!isPlainObject(obj)) return obj;

  const target: { [name: string]: any } = {};

  for (const key in obj) {
    const val = obj[key];
    if (typeof val !== 'undefined') {
      target[key] = removeUndefineds(val);
    }
  }

  return target as T;
}

export const range = (start: number, end: number) =>
  Array.from(Array(Math.abs(end - start) + 1), (_, i) => start + i);

export const countDown = (timer: number) => {
  const totalSeconds = (timer %= 3600);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return {
    minutes,
    seconds
  };
};

export const fileToObjectUrl = (image: File | string) =>
  image instanceof File ? URL.createObjectURL(image) : image;

export const getGender = (gender: any, genderOther: string) => {
  const genderLabel = GENDER_OPTIONS.find((item: ISelectOptions) => item.value === gender);
  return gender === 'OTHERS' ? genderOther : genderLabel?.label;
};

export const getDobAge = (dateOfBirth: Date) => {
  const dob = format(new Date(dateOfBirth), 'dd/MM/yyy');
  const age = differenceInYears(new Date(), new Date(dateOfBirth));

  return {
    dob,
    age,
    dobAge: `${dob} - ${age} y`
  };
};

export const formatAge = (date_of_birth: string | Date) => {
  if (!date_of_birth) return '-';

  const d = new Date(date_of_birth);
  if (Number.isNaN(d.getTime())) return '-';

  const age = differenceInYears(new Date(), d);
  return age;
};

export const getDateOrdinal = (date: any) => {
  const curDate = new Date(date);
  const day = curDate.getDate();
  const month = curDate.toLocaleString('default', { month: 'long' });
  const year = curDate.getFullYear();

  const nthNumber = (number: number) => {
    if (number > 3 && number < 21) return 'th';
    switch (number % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  };

  const newDate = `${day}${nthNumber(day)} ${month}, ${year}`;

  return newDate;
};

export const getConditions = (user: IPatientDetails | null) => {
  let conditions = '-';

  if (user) {
    let arrConditions: string[] = [];
    if (user.conditions) {
      arrConditions = JSON.parse(user.conditions);
    }

    if (user.other_conditions) arrConditions.push(user.other_conditions);
    conditions = arrConditions.join(', ');
  }

  return conditions;
};

export const getMedicalHistory = (history: string, others: string) => {
  let medicalHistory = '-';

  if (history || others) {
    let arrHistory: string[] = [];
    if (history) {
      arrHistory = JSON.parse(history);
    }

    if (others) arrHistory.push(others);
    medicalHistory = arrHistory.join(', ');
  }

  return medicalHistory;
};

export const getProfilePicInput = (profilePic: string | null | undefined) => {
  let profilePicUrl = profilePic;

  if (profilePic && profilePic.includes('https://')) {
    const split = profilePic.split('/');
    const splitSource = split[4].split('?');
    profilePicUrl = `${split[3]}/${splitSource[0]}`;
  }

  return profilePicUrl;
};

export const fileChecker = ({
  file,
  fileTypes = [],
  fileSizeLimit
}: {
  file: File;
  fileTypes: string[];
  fileSizeLimit: number;
}) => {
  let response: IFileCheckResponse = {
    success: true,
    message: ''
  };

  if (file) {
    // @ts-ignore
    const ext = file.name.split('.').pop().toLowerCase();
    const validFileTypes = fileTypes;

    if (validFileTypes.includes(ext)) {
      const sizeLimit = fileSizeLimit / 1024;
      const sizeLimitByte = fileSizeLimit * 1000;
      const decimals = '';
      const k = 1024;
      const dm: number = decimals || 2;
      const sizes: string[] = ['Bytes', 'KB', 'MB'];
      const bigSizes: any = ['GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
      const i = Math.floor(Math.log(file.size) / Math.log(k));
      const iC = Math.floor(Math.log(sizeLimitByte) / Math.log(k));
      const fileSize = parseFloat((file.size / Math.pow(k, i)).toFixed(dm));

      if ((fileSize > sizeLimit && sizes[i] === 'MB') || bigSizes.includes(sizes[i], bigSizes)) {
        const fSize = sizes[i];
        const cSize = sizes[iC];
        const limit = iC > 1 ? Math.round(sizeLimit) : fileSizeLimit;
        response = {
          success: false,
          message: `${file.name} has ${fileSize}${fSize} of file size. Please limit file size to ${limit}${cSize} and under only.`
        };
      }
    } else {
      response = {
        success: false,
        message: `${file.name} is not a valid file. We only accept ${validFileTypes.join(', ')}.`
      };
    }
  }

  return response;
};

export const checkValidNumber = (input: any) => input.match(/^[0-9]+$/);

export const getRangeDates = (start: Date, end: Date) => {
  const date = new Date(start.getTime());
  const dates = [];

  while (date <= end) {
    dates.push(new Date(date));
    date.setDate(date.getDate() + 1);
  }

  return dates;
};

export const groupBy = (array: any[], f: any) => {
  // eslint-disable-next-line no-sequences
  return array.reduce((r, v, i, a, k = f(v)) => ((r[k] || (r[k] = [])).push(v), r), {});
};

export const photoPlaceholder: {
  patient: string;
  doctor: string;
} = {
  patient: PhotoPlaceholderPatient,
  doctor: PhotoPlaceholderDoctor
};

export const fullName = (user: any) =>
  user ? `${user?.first_name || '-'} ${user?.last_name || '-'}` : '-';

export const calendarDate = ({
  date,
  view,
  dateFormat = 'dd, MMMM yyyy'
}: {
  date: Date;
  view?: TCalendarView;
  dateFormat?: string;
}) => {
  let formatDate = format(date, dateFormat);
  let from = format(date, 'yyyy-MM-dd 00:00:00');
  let to = format(date, 'yyyy-MM-dd 23:59:59');

  if (view === 'dayGridMonth') {
    formatDate = `${format(startOfMonth(date), dateFormat)} - ${format(
      endOfMonth(date),
      dateFormat
    )}`;

    from = format(startOfMonth(date), 'yyyy-MM-dd 00:00:00');
    to = format(endOfMonth(date), 'yyyy-MM-dd 23:59:59');
  }

  if (view === 'timeGridWeek') {
    formatDate = `${format(startOfWeek(date, { weekStartsOn: 1 }), dateFormat)} - ${format(
      endOfWeek(date, { weekStartsOn: 1 }),
      dateFormat
    )}`;

    from = format(startOfWeek(date), 'yyyy-MM-dd 00:00:00');
    to = format(endOfWeek(date, { weekStartsOn: 1 }), 'yyyy-MM-dd 23:59:59');
  }

  return {
    formatDate,
    fromUtc: new Date(from).toISOString(),
    from,
    to,
    toUtc: new Date(to).toISOString()
  };
};

export const getAddress = (user: any) =>
  user?.street && user?.state && user?.city && user?.postcode
    ? `${user?.street || '-'} ${user?.city || '-'}, ${user?.state || '-'}, ${user?.postcode || '-'}`
    : '';
export const getPatientAddress = (user: IPatientDetails | null) =>
  user?.street && user?.state && user?.city && user?.postcode
    ? `${user?.street || '-'} ${user?.city || '-'}, ${user?.state || '-'}, ${user?.postcode || '-'}`
    : '';

export const downloadFile = (fileName: string, fileUrl: string) => {
  try {
    const link = document.createElement('a');
    link.href = `${CONF_API_URL}download?url=${encodeURIComponent(
      fileUrl
    )}&filename=${encodeURIComponent(fileName)}`;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error('Download failed:', error);
  }
};
export const formatId = (value: number, size: number) => {
  let s = value + '';
  while (s.length < size) s = '0' + s;
  return s;
};

export const checkBooleanString = (value?: string) => ({
  stringValue: value ? (JSON.parse(value) ? 'Yes' : 'No') : '-',
  isTrue: value ? JSON.parse(value) : false
});

export const checkBoolean = (value?: 1 | 2) => ({
  stringValue: value ? (value === 1 ? 'Yes' : 'No') : '-',
  isTrue: value ? (value === 1 ? true : false) : false
});

export const getHealthCondition = (healthHistory?: IPatientHistory) => ({
  diagnosed_conditions: healthHistory?.diagnosed_conditions
    ? JSON.parse(healthHistory.diagnosed_conditions).map((item: any) => {
        return item.value;
      })
    : ([] as string[]),
  medication_list: healthHistory?.medication_list
    ? JSON.parse(healthHistory.medication_list).map((item: any) => {
        return `<div>
        <span className="font-semibold">${item.name} - </span>
        <span>${item.dosage} | </span>
        <span>${item.frequency} | </span>
        <span>${item.reason}</span>
        </div>`;
      })
    : ([] as string[]),
  practitioner_contact_detail: healthHistory?.practitioner_contact_details
    ? JSON.parse(healthHistory.practitioner_contact_details).map((item: any) => {
        return `<div>
             <span className="font-semibold">${item.name} - </span>
             <span>${item.specialty}</span>
            <span>${item.contact}</span>
        </div>`;
      })
    : ([] as string[])
});

export const getPreExistingConditions = (healthHistory?: IPatientHistory) => ({
  any_other_condition_aware_of_auto_immunity:
    healthHistory?.any_other_condition_aware_of_auto_immunity
      ? (JSON.parse(healthHistory.any_other_condition_aware_of_auto_immunity) as string[])
      : ([] as string[]),
  any_other_condition_aware_of_cardio: healthHistory?.any_other_condition_aware_of_cardio
    ? (JSON.parse(healthHistory.any_other_condition_aware_of_cardio) as string[])
    : ([] as string[]),
  any_other_condition_aware_of_derma: healthHistory?.any_other_condition_aware_of_derma
    ? (JSON.parse(healthHistory.any_other_condition_aware_of_derma) as string[])
    : ([] as string[]),
  any_other_condition_aware_of_gastro: healthHistory?.any_other_condition_aware_of_gastro
    ? (JSON.parse(healthHistory.any_other_condition_aware_of_gastro) as string[])
    : ([] as string[]),
  any_other_condition_aware_of_mental: healthHistory?.any_other_condition_aware_of_mental
    ? (JSON.parse(healthHistory.any_other_condition_aware_of_mental) as string[])
    : ([] as string[]),
  any_other_condition_aware_of_metabolic: healthHistory?.any_other_condition_aware_of_metabolic
    ? (JSON.parse(healthHistory.any_other_condition_aware_of_metabolic) as string[])
    : ([] as string[]),
  any_other_condition_aware_of_muskolo: healthHistory?.any_other_condition_aware_of_muskolo
    ? (JSON.parse(healthHistory.any_other_condition_aware_of_muskolo) as string[])
    : ([] as string[]),
  any_other_condition_aware_of_nuero: healthHistory?.any_other_condition_aware_of_nuero
    ? (JSON.parse(healthHistory.any_other_condition_aware_of_nuero) as string[])
    : ([] as string[]),
  any_other_condition_aware_of_reproductive:
    healthHistory?.any_other_condition_aware_of_reproductive
      ? (JSON.parse(healthHistory.any_other_condition_aware_of_reproductive) as string[])
      : ([] as string[]),
  any_other_condition_aware_of_urinary: healthHistory?.any_other_condition_aware_of_urinary
    ? (JSON.parse(healthHistory.any_other_condition_aware_of_urinary) as string[])
    : ([] as string[]),
  natural_therapies_alternatives: healthHistory?.natural_therapies_alternatives
    ? (JSON.parse(healthHistory.natural_therapies_alternatives) as string[])
    : ([] as string[]),
  investigation_surgical_intervention: healthHistory?.investigation_surgical_intervention
    ? (JSON.parse(healthHistory.investigation_surgical_intervention) as string[])
    : ([] as string[]),
  mens_blood_color: healthHistory?.mens_blood_color
    ? (JSON.parse(healthHistory.mens_blood_color) as string[])
    : ([] as string[]),
  mens_apperance: healthHistory?.mens_apperance
    ? (JSON.parse(healthHistory.mens_apperance) as string[])
    : ([] as string[]),
  investigation_surgical_surgeries: healthHistory?.investigation_surgical_surgeries
    ? (JSON.parse(healthHistory.investigation_surgical_surgeries) as string[])
    : ([] as string[]),
  medication_list: healthHistory?.medication_list
    ? (JSON.parse(healthHistory.medication_list) as IMedicationProps[])
    : ([] as string[])
});

export const getMedicineDetails = (healthHistory?: IPatientHistory) => ({
  // withPrescription: healthHistory?.medicine_with_prescription_details
  //   ? `Frequency: ${
  //       (JSON.parse(healthHistory.medicine_with_prescription_details) as IMedicineDetails).frequency
  //     }, Type: ${
  //       (JSON.parse(healthHistory.medicine_with_prescription_details) as IMedicineDetails).type
  //     }`
  //   : '-',
  // withoutPrescription: healthHistory?.medicine_without_prescription_details
  //   ? `Frequency: ${
  //       (JSON.parse(healthHistory.medicine_without_prescription_details) as IMedicineDetails)
  //         .frequency
  //     }, Type: ${
  //       (JSON.parse(healthHistory.medicine_without_prescription_details) as IMedicineDetails).type
  //     }`
  //   : '-'
  medication: healthHistory?.medication_list
    ? `Name: ${(JSON.parse(healthHistory.medication_list) as IMedicationProps).name},
     Dosage: ${(JSON.parse(healthHistory.medication_list) as IMedicationProps).dosage}
      Frequency: ${(JSON.parse(healthHistory.medication_list) as IMedicationProps).frequency}
      Reason: ${(JSON.parse(healthHistory.medication_list) as IMedicationProps).reason}`
    : '-'
});

export const getHolisticHealth = (healthHistory?: IPatientHistory) => ({
  // diet: healthHistory?.current_diet ? healthHistory?.current_diet : '-',
  // activityLevel: healthHistory?.physical_activity_level
  //   ? healthHistory?.physical_activity_level
  //   : '-',
  // emotions: healthHistory?.stress_emotions ? healthHistory?.stress_emotions : '-'
});

export const getAdditionalInformation = (patient?: IPatientDetails) => ({
  // achieve: patient?.profile?.achieve_polln ? patient?.profile?.achieve_polln : '-',
  // share: patient?.profile?.addtl_info ? patient?.profile?.addtl_info : '-',
  // heard: patient?.profile?.about_polln ? patient?.profile?.about_polln : '-'
  mens_apperance: patient?.health_history?.mens_apperance
    ? (JSON.parse(patient?.health_history.mens_apperance) as string[])
    : ([] as string[]),
  mens_blood_color: patient?.health_history?.mens_blood_color
    ? (JSON.parse(patient?.health_history.mens_blood_color) as string[])
    : ([] as string[])
});

export const getPatientBackground = (background?: IPatientBackground) => ({
  approach_your_health: background?.approach_your_health
    ? (background.approach_your_health as string)
    : '',
  diagnosed_health_condition: background?.diagnosed_health_condition
    ? (JSON.parse(background?.diagnosed_health_condition) as string[])
    : ([] as string[]),
  diagnosed_health_condition_others: background?.diagnosed_health_condition_others
    ? (background.diagnosed_health_condition_others as string)
    : '',
  gastro: background?.gastro ? (JSON.parse(background?.gastro) as string[]) : ([] as string[]),
  health_goals: background?.health_goals
    ? (JSON.parse(background?.health_goals) as string[])
    : ([] as string[]),
  health_goals_others: background?.health_goals_others
    ? (background.health_goals_others as string)
    : '',
  hear_about: background?.hear_about ? (background.hear_about as string) : '',
  interested_speciality: background?.interested_speciality
    ? (background.interested_speciality as string)
    : '',
  menopausal: background?.menopausal
    ? (JSON.parse(background?.menopausal) as string[])
    : ([] as string[]),
  menstruation: background?.menstruation
    ? (JSON.parse(background?.menstruation) as string[])
    : ([] as string[]),
  mood: background?.mood ? (JSON.parse(background?.mood) as string[]) : ([] as string[]),
  other: background?.other ? (JSON.parse(background?.other) as string[]) : ([] as string[]),
  other_symptoms: background?.other_symptoms ? (background.other_symptoms as string) : '',
  pain: background?.pain ? (JSON.parse(background?.pain) as string[]) : ([] as string[]),
  sexual_health: background?.sexual_health
    ? (JSON.parse(background?.sexual_health) as string[])
    : ([] as string[]),
  undiagnosed_health_condition: background?.undiagnosed_health_condition
    ? (background.undiagnosed_health_condition as string)
    : '',
  vaginal_health: background?.vaginal_health
    ? (JSON.parse(background?.vaginal_health) as string[])
    : ([] as string[]),
  virtual_not_appropriate_reason: background?.virtual_not_appropriate_reason
    ? (background.virtual_not_appropriate_reason as string)
    : '',
  what_brings_you_to_hazel: background?.what_brings_you_to_hazel
    ? (background.what_brings_you_to_hazel as string)
    : '',
  whatelse_interested: background?.whatelse_interested
    ? (JSON.parse(background?.whatelse_interested) as string[])
    : ([] as string[]),
  whatelse_interested_others: background?.whatelse_interested_others
    ? (background.whatelse_interested_others as string)
    : '',
  experiencing_any_pain: background?.experiencing_any_pain
    ? (JSON.parse(background.experiencing_any_pain) as string[])
    : ([] as string[]),
  experiencing_any_pain_other: background?.experiencing_any_pain_other
    ? (background.experiencing_any_pain_other as string)
    : ''
});

export const getFrequencyMenstrual = (frequency: string) => {
  let patientFrequency: string = '';
  switch (frequency) {
    case '0':
      patientFrequency = 'Irregular cycle - shorter than 21 days, longer than 35 or inconsistent';
      break;
    case '1':
      patientFrequency = 'Regular cycle - every 22- 34 days';
      break;
    case '2':
      patientFrequency =
        'Amenorrhea -absence of more than 3 menstrual cycles after having regular periods';
      break;
    case '3':
      patientFrequency = 'Determined by contraception- my cycle is influenced by my contraception';
      break;
    case '4':
      patientFrequency = "I've never had a period";
      break;
    case '5':
      patientFrequency = 'I am no longer having periods';
      break;
    case '6':
      patientFrequency = "I'm not sure";
      break;
    default:
      break;
  }
  return patientFrequency;
};

export const getPeriodLast = (period: string) => {
  let periodLast: string = '';
  switch (period) {
    case '0':
      periodLast = '1-3 days';
      break;
    case '1':
      periodLast = '3-7 days';
      break;
    case '2':
      periodLast = 'More than 7 days';
      break;
    case '3':
      periodLast = "I'm not sure";
      break;
    default:
      break;
  }
  return periodLast;
};

export const getMentrualFlow = (flow: string) => {
  let mentrualFlow: string = '';
  switch (flow) {
    case '0':
      mentrualFlow = 'Heavy flow - changing sanitary products every 1 -2 hours';
      break;
    case '1':
      mentrualFlow = 'Moderate flow - changing sanitary products every 3 hours';
      break;
    case '2':
      mentrualFlow = 'Light flow - changing sanitary products every 4 - 5+ hours';
      break;
    case '3':
      mentrualFlow = 'Spotting - requiring only a sanitary liner or less to manage';
      break;
    case '4':
      mentrualFlow = 'Varies - combination of the above or varies across cycles';
      break;
    case '5':
      mentrualFlow = "I'm not sure";
      break;
    default:
      break;
  }
  return mentrualFlow;
};

export const getMenstrualPain = (pain: string) => {
  let painLevel: string = '';
  switch (pain) {
    case '0':
      painLevel = '0 No pain';
      break;
    case '1':
      painLevel = '1-3 Mild pain: noticeable but does not interfere with daily activities';
      break;
    case '2':
      painLevel = '4-6 Moderate pain: more noticeable and may interfere with daily activities';
      break;
    case '3':
      painLevel =
        '7-9 Severe pain: intense and limits daily activities, making it difficult to concentrate or sleep';
      break;
    case '4':
      painLevel = '10 Worst pain imaginable: unbearable and may cause nausea or vomiting';
      break;
    case '5':
      painLevel = "I'm not sure";
      break;
    default:
      break;
  }
  return painLevel;
};

export const getProfessionals = (professionals: string) => {
  let professionalsAns: string = '';
  switch (professionals) {
    case '0':
      professionalsAns = "I'm currently seeing healthcare professionals";
      break;
    case '1':
      professionalsAns =
        "I'm not currently seeing healthcare professionals, but I have in the past";
      break;
    case '2':
      professionalsAns =
        "I'm managing myself and have never seen healthcare professionals for these conditions/symptoms";
      break;
    default:
      break;
  }
  return professionalsAns;
};

export const getBooleanValue = (value: string) => {
  const answer: string = value === '1' ? 'Yes' : 'No';

  return answer;
};

export const getNextSchedules = (createSchedule: ICreateSchedule): [IDoctorDateSchedule?] => {
  let schedules: [IDoctorDateSchedule?] = [];
  for (let day of createSchedule.days) {
    let schedule = null;
    switch (day) {
      case 'MON':
        schedule = {
          start_at: nextMonday(createSchedule.start_at),
          end_at: nextMonday(createSchedule.end_at),
          recurring_id: createSchedule.recurring_id
        };
        break;
      case 'TUE':
        schedule = {
          start_at: nextTuesday(createSchedule.start_at),
          end_at: nextTuesday(createSchedule.end_at),
          recurring_id: createSchedule.recurring_id
        };
        break;
      case 'WED':
        schedule = {
          start_at: nextWednesday(createSchedule.start_at),
          end_at: nextWednesday(createSchedule.end_at),
          recurring_id: createSchedule.recurring_id
        };
        break;
      case 'THU':
        schedule = {
          start_at: nextThursday(createSchedule.start_at),
          end_at: nextThursday(createSchedule.end_at),
          recurring_id: createSchedule.recurring_id
        };
        break;
      case 'FRI':
        schedule = {
          start_at: nextFriday(createSchedule.start_at),
          end_at: nextFriday(createSchedule.end_at),
          recurring_id: createSchedule.recurring_id
        };
        break;
      case 'SAT':
        schedule = {
          start_at: nextSaturday(createSchedule.start_at),
          end_at: nextSaturday(createSchedule.end_at),
          recurring_id: createSchedule.recurring_id
        };
        break;
      case 'SUN':
        schedule = {
          start_at: nextSunday(createSchedule.start_at),
          end_at: nextSunday(createSchedule.end_at),
          recurring_id: createSchedule.recurring_id
        };
        break;
      default:
        break;
    }
    if (schedule) {
      schedules.push(schedule);
    }
  }
  return schedules;
};

export const lifestyleLabel = (value: string): string => {
  const exists = LIFESTYLE_ADVICE.find((advice) => advice.value === value);
  if (exists) {
    return exists.label;
  } else {
    return '-';
  }
};

export const referalsTypeLabel = (value: string): string => {
  const exists = REFERRAL_TYPE.find((advice) => advice.value === value);
  if (exists) {
    return exists.label;
  } else {
    return '-';
  }
};

export const referalsTimeframeLabel = (value: string): string => {
  const exists = REFERRAL_TIMEFRAME.find((advice) => advice.value === value);
  if (exists) {
    return exists.label;
  } else {
    return '-';
  }
};

export const checkDate = (inputDate: Date) => {
  const today = startOfDay(new Date());
  const isoDate = typeof inputDate === 'string' ? inputDate : inputDate.toISOString();
  const compareDate = startOfDay(parseISO(isoDate));

  return isBefore(compareDate, today) ? false : true;
};

export const getOptionHealthHistory = (value: string | any, options: string[]) => {
  const index = parseInt(value, 10);

  if (Number.isInteger(index) && index >= 0 && index < options.length) {
    return options[index];
  }

  return false;
};

export const handleDownloadLink = (url: string) => {
  const link = document.createElement('a');
  link.href = url;

  document.body.appendChild(link);

  link.click();

  if (link.parentNode) link.parentNode.removeChild(link);
};

export const numberToWords = (num) => {
  const ones = [
    'Zero',
    'One',
    'Two',
    'Three',
    'Four',
    'Five',
    'Six',
    'Seven',
    'Eight',
    'Nine',
    'Ten',
    'Eleven',
    'Twelve',
    'Thirteen',
    'Fourteen',
    'Fifteen',
    'Sixteen',
    'Seventeen',
    'Eighteen',
    'Nineteen'
  ];

  const tens = [
    '',
    '',
    'Twenty',
    'Thirty',
    'Forty',
    'Fifty',
    'Sixty',
    'Seventy',
    'Eighty',
    'Ninety'
  ];

  if (num < 0 || num >= 100 || isNaN(num)) return '';
  if (num < 20) return ones[num];
  return tens[Math.floor(num / 10)] + (num % 10 !== 0 ? ' ' + ones[num % 10] : '');
};

export const displayConsultationStatus = (
  status: TConsultationStatus,
  isCurrentDoctor: boolean
) => {
  switch (status) {
    case 'DONE':
      return {
        label: 'Completed',
        textColor: 'text-white',
        backgroundColor: 'bg-hazel-light-gray-200'
      };
    case 'CALLEND':
      return {
        label: `Call End ${isCurrentDoctor ? `- Re-enter Consult` : ''}`,
        textColor: 'text-hazel-light-gray-200',
        backgroundColor: 'bg-hazel-red-300/40'
      };
    case 'CANCEL':
      return {
        label: 'Cancelled',
        textColor: 'text-white',
        backgroundColor: 'bg-hazel-light-gray-200'
      };
    case 'DIDNOTATTEND':
      return {
        label: 'Cancelled',
        textColor: 'text-white',
        backgroundColor: 'bg-hazel-light-gray-200'
      };
    case 'PRECALL':
      return {
        label: `Precall ${isCurrentDoctor ? `- Enter Consult` : ''}`,
        textColor: 'text-hazel-light-gray-200',
        backgroundColor: 'bg-hazel-yellow-100'
      };
    case 'ONCALL':
      return {
        label: `On Call ${isCurrentDoctor ? `- Re-enter Consult` : ''}`,
        textColor: 'text-hazel-light-gray-200',
        backgroundColor: 'bg-hazel-yellow-100'
      };
    default:
      return {
        label: status,
        textColor: 'text-white',
        backgroundColor: 'bg-hazel-light-gray-200'
      };
  }
};

export const formatDateFix = (dateString: string | Date | null | undefined): string => {
  if (!dateString) return 'N/A';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid date';
    return format(date, 'dd MMM yyyy HH:mm');
  } catch (error) {
    return 'Invalid date';
  }
};

export const inboxStatus = (status: string) => {
  const itemStatus = status?.toLowerCase();
  switch (itemStatus) {
    case 'urgent':
      return {
        label: 'Urgent',
        textColor: 'text-hazel-light-gray-200',
        backgroundColor: 'bg-red-500/70'
      };
    case 'non_urgent':
      return {
        label: 'Non-Urgent',
        textColor: 'text-hazel-light-gray-200',
        backgroundColor: 'bg-[#FFDDB9]'
      };
    case 'high':
      return {
        label: 'High',
        textColor: 'text-hazel-light-gray-200',
        backgroundColor: 'bg-red-500/70'
      };
    case 'medium':
      return {
        label: 'Medium',
        textColor: 'text-hazel-light-gray-200',
        backgroundColor: 'bg-[#FFDDB9]'
      };
    case 'low':
      return {
        label: 'Low',
        textColor: 'text-hazel-solid-green-600',
        backgroundColor: 'bg-hazel-solid-green-600/20'
      };
    default:
      return {
        label: status || 'Unknown',
        textColor: 'text-hazel-light-gray-200',
        backgroundColor: 'bg-[#ecefe9]'
      };
  }
};

export const getInboxStatusLabel = (value: string) => {
  switch (value) {
    case 'pending':
      return 'bg-[#FF9260]';
    case 'urgent':
      return 'bg-red-500';
    case 'follow-up':
      return 'bg-hazel-orange-100';
  }
};

export const getTagInboxStatusLabel = (value: string) => {
  switch (value) {
    case 'Results':
      return {
        textColor: 'text-[#1C274C]',
        backgroundColor: 'bg-[#0000FF]/20'
      };
    case 'Referral':
      return {
        textColor: 'text-[#1C274C]',
        backgroundColor: 'bg-[#0000FF]/20'
      };
    case 'ID Document':
      return {
        textColor: 'text-[#1C274C]',
        backgroundColor: 'bg-[#0000FF]/20'
      };
    case 'Letter':
      return {
        textColor: 'text-[#1C274C]',
        backgroundColor: 'bg-[#0000FF]/20'
      };
    case 'Normal':
      return {
        textColor: 'text-hazel-solid-green-600',
        backgroundColor: 'bg-hazel-solid-green-600/20'
      };
    case 'Abnormal':
      return {
        textColor: 'text-hazel-light-gray-200',
        backgroundColor: 'bg-red-500/70'
      };
    default:
      return {
        textColor: 'text-hazel-light-gray-200',
        backgroundColor: 'bg-[#ecefe9]'
      };
  }
};
